import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Search, Check } from 'lucide-react';
import DatePicker from '../ui/DatePicker';
import { Label } from '../ui/label';

const DEFAULT_PUJA_TYPES = [
  'Vedic Archana',
  'Hawan & Yajna',
  'Abhishekam',
  'Sahasranama Chanting',
  'Tantrik & Shodashopachara',
  'Daan & Gau Seva',
  'Shanti & Dosha Nivaran',
  'One-Time Ritual',
  'Weekly Ritual'
];

const DEFAULT_BENEFITS = [
  'Health & Longevity',
  'Wealth & Prosperity',
  'Career & Business Growth',
  'Marriage & Relationship',
  'Protection from Negative Energies & Evil Eye',
  'Peace of Mind & Spiritual Growth',
  'Child Welfare & Family Harmony',
  'Education & Knowledge',
  'Pitra Dosh Nivaran',
  'Graha Shanti & Planetary Relief'
];

const DEFAULT_WEEKLY_DAYS = [
  'Monday (Somvar)',
  'Tuesday (Mangalvar)',
  'Wednesday (Budhvar)',
  'Thursday (Guruvar)',
  'Friday (Shukravar)',
  'Saturday (Shanivar)',
  'Sunday (Ravivar)'
];

const DEFAULT_TITHIS = [
  'Ekadashi',
  'Purnima',
  'Amavasya',
  'Pradosh Vrat',
  'Sankashti Chaturthi',
  'Masik Shivratri',
  'Navratri',
  'Ashtami',
  'Janmashtami',
  'Somvati Amavasya'
];

const TABS = [
  { id: 'puja_type', label: 'Puja Type' },
  { id: 'deity', label: 'Deity' },
  { id: 'benefit', label: 'Benefit' },
  { id: 'temple_type', label: 'Temple Type' },
  { id: 'weekly', label: 'Weekly' },
  { id: 'tithi', label: 'Upcoming Tithi' },
  { id: 'date_range', label: 'Date Range' }
];

export default function PujaFilterModal({
  isOpen,
  onClose,
  filters,
  onApply,
  onClear,
  availableDeities = [],
  availableTempleTypes = []
}) {
  const [activeTab, setActiveTab] = useState('deity');
  const [tabSearch, setTabSearch] = useState('');

  // Local draft of filters so user can change and apply
  const [draft, setDraft] = useState({
    pujaTypes: filters?.pujaTypes || [],
    deities: filters?.deities || [],
    benefits: filters?.benefits || [],
    templeTypes: filters?.templeTypes || [],
    weeklyDays: filters?.weeklyDays || [],
    tithis: filters?.tithis || [],
    startDate: filters?.startDate || '',
    endDate: filters?.endDate || ''
  });

  // Sync draft when opened
  React.useEffect(() => {
    if (isOpen) {
      setDraft({
        pujaTypes: filters?.pujaTypes || [],
        deities: filters?.deities || [],
        benefits: filters?.benefits || [],
        templeTypes: filters?.templeTypes || [],
        weeklyDays: filters?.weeklyDays || [],
        tithis: filters?.tithis || [],
        startDate: filters?.startDate || '',
        endDate: filters?.endDate || ''
      });
      setTabSearch('');
    }
  }, [isOpen, filters]);

  if (!isOpen) return null;

  const toggleItem = (key, item) => {
    setDraft(prev => {
      const currentList = prev[key] || [];
      const exists = currentList.includes(item);
      const updated = exists
        ? currentList.filter(x => x !== item)
        : [...currentList, item];
      return { ...prev, [key]: updated };
    });
  };

  const handleApply = () => {
    if (draft.startDate && draft.endDate && draft.startDate > draft.endDate) return;
    onApply(draft);
    onClose();
  };

  const handleClearAll = () => {
    const cleared = {
      pujaTypes: [],
      deities: [],
      benefits: [],
      templeTypes: [],
      weeklyDays: [],
      tithis: [],
      startDate: '',
      endDate: ''
    };
    setDraft(cleared);
    onClear();
  };

  // Get options for current tab
  const getOptionsForTab = () => {
    let list = [];
    if (activeTab === 'puja_type') list = DEFAULT_PUJA_TYPES;
    else if (activeTab === 'deity') list = availableDeities.filter(d => d !== 'All');
    else if (activeTab === 'benefit') list = DEFAULT_BENEFITS;
    else if (activeTab === 'temple_type') list = availableTempleTypes.filter(t => t !== 'All');
    else if (activeTab === 'weekly') list = DEFAULT_WEEKLY_DAYS;
    else if (activeTab === 'tithi') list = DEFAULT_TITHIS;

    if (!tabSearch.trim()) return list;
    return list.filter(item => item.toLowerCase().includes(tabSearch.toLowerCase()));
  };

  const getDraftKeyForTab = () => {
    switch (activeTab) {
      case 'puja_type': return 'pujaTypes';
      case 'deity': return 'deities';
      case 'benefit': return 'benefits';
      case 'temple_type': return 'templeTypes';
      case 'weekly': return 'weeklyDays';
      case 'tithi': return 'tithis';
      default: return '';
    }
  };

  const getSelectedCountForTab = (tabId) => {
    switch (tabId) {
      case 'puja_type': return draft.pujaTypes?.length || 0;
      case 'deity': return draft.deities?.length || 0;
      case 'benefit': return draft.benefits?.length || 0;
      case 'temple_type': return draft.templeTypes?.length || 0;
      case 'weekly': return draft.weeklyDays?.length || 0;
      case 'tithi': return draft.tithis?.length || 0;
      case 'date_range': return (draft.startDate || draft.endDate) ? 1 : 0;
      default: return 0;
    }
  };

  const activeTabHeader = TABS.find(t => t.id === activeTab)?.label?.toUpperCase() || 'FILTERS';
  const options = getOptionsForTab();
  const draftKey = getDraftKeyForTab();

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="puja-filters-title" className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-gray-200 max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 id="puja-filters-title" className="font-heading text-lg font-bold text-[#1A202C]">Filters</h2>
          <button
            aria-label="Close filters"
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 2-Column Body */}
        <div className="flex flex-1 overflow-hidden min-h-[380px]">
          {/* Left Column: Categories List */}
          <div className="w-28 sm:w-52 shrink-0 bg-[#F8F9FA] border-r border-gray-200 flex flex-col overflow-y-auto py-2">
            {TABS.map(tab => {
              const isActive = activeTab === tab.id;
              const count = getSelectedCountForTab(tab.id);

              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setTabSearch('');
                  }}
                  className={`w-full text-left px-3 sm:px-5 py-3 text-xs transition-colors flex items-center justify-between relative ${
                    isActive
                      ? 'bg-white font-bold text-gray-900 border-l-[3.5px] border-[#EA8A2A] shadow-xs'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/60 font-medium'
                  }`}
                >
                  <span className="truncate">{tab.label}</span>
                  {count > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.2 bg-[#EA8A2A]/10 text-[#EA8A2A] text-[10px] font-black rounded-full">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Right Column: Tab Content */}
          <div className="flex-1 min-w-0 p-3 sm:p-5 overflow-y-auto flex flex-col bg-white">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2.5">
              {activeTabHeader}
            </h3>

            {/* If tab is Date Range */}
            {activeTab === 'date_range' ? (
              <div className="space-y-4 pt-1 max-w-sm">
                <div>
                  <Label htmlFor="puja-filter-from" className="text-xs font-semibold text-gray-700 mb-1 block">From Date</Label>
                  <DatePicker id="puja-filter-from" value={draft.startDate} maxDate={draft.endDate} withPortal
                    onChange={startDate => setDraft(prev => ({ ...prev, startDate }))} />
                </div>

                <div>
                  <Label htmlFor="puja-filter-to" className="text-xs font-semibold text-gray-700 mb-1 block">To Date</Label>
                  <DatePicker id="puja-filter-to" value={draft.endDate} minDate={draft.startDate} withPortal
                    onChange={endDate => setDraft(prev => ({ ...prev, endDate }))} />
                </div>

                {draft.startDate && draft.endDate && draft.startDate > draft.endDate && (
                  <p role="alert" className="text-xs text-red-600">To Date must be on or after From Date.</p>
                )}

                {(draft.startDate || draft.endDate) && (
                  <button
                    type="button"
                    onClick={() => setDraft(prev => ({ ...prev, startDate: '', endDate: '' }))}
                    className="text-xs font-bold text-[#EA8A2A] hover:underline"
                  >
                    Clear dates
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Search Box inside Tab */}
                <div className="relative mb-3">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={tabSearch}
                    onChange={(e) => setTabSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 text-xs rounded-xl border border-gray-200 outline-none focus:border-[#EA8A2A] focus:ring-1 focus:ring-[#EA8A2A]/20 transition-all text-[#2A1F16]"
                  />
                  {tabSearch && (
                    <button
                      onClick={() => setTabSearch('')}
                      className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
                    >
                      &times;
                    </button>
                  )}
                </div>

                {/* Checkbox Options List */}
                <div className="flex-1 overflow-y-auto space-y-1 pr-1 max-h-72">
                  {options.length === 0 ? (
                    <p className="text-xs text-gray-400 py-6 text-center">No matching options found</p>
                  ) : (
                    options.map((item) => {
                      const isSelected = draft[draftKey]?.includes(item);

                      return (
                        <label
                          key={item}
                          className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer select-none transition-colors group"
                        >
                          <div
                            className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${
                              isSelected
                                ? 'bg-[#EA8A2A] border-[#EA8A2A] text-white shadow-2xs'
                                : 'border-gray-300 bg-white group-hover:border-gray-400'
                            }`}
                          >
                            {isSelected && <Check className="w-3 h-3 stroke-[3]" />}
                          </div>
                          <input
                            type="checkbox"
                            checked={isSelected || false}
                            onChange={() => toggleItem(draftKey, item)}
                            className="sr-only"
                          />
                          <span className={`text-xs ${isSelected ? 'font-bold text-gray-900' : 'font-normal text-gray-700'}`}>
                            {item}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white">
          <button
            type="button"
            onClick={handleClearAll}
            className="text-xs font-semibold text-gray-500 hover:text-gray-900 underline transition-colors cursor-pointer"
          >
            Clear All
          </button>

          <button
            type="button"
            onClick={handleApply}
            disabled={Boolean(draft.startDate && draft.endDate && draft.startDate > draft.endDate)}
            className="bg-[#EA8A2A] hover:bg-[#d97d22] text-white px-7 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all active:scale-[0.98] cursor-pointer"
          >
            Apply Filters
          </button>
        </div>
      </motion.div>
    </div>
  );
}
